import AsyncStorage from '@react-native-async-storage/async-storage';
import { create } from 'zustand';
import { STORAGE_ONBOARDING_COMPLETED_AT_MS } from '@/constants/onboarding';
import type { PlanEntitlement, PlanTier } from '@/types/entitlements';

export type TravelType = 'solo' | 'group' | 'business' | 'couple' | 'family';

export type AvatarSelection =
  | { type: 'preset'; id: string }
  | { type: 'upload'; uri: string };

export type NotificationChannel = 'push' | 'email' | 'inApp';

export type NotificationCategory = 'itinerary' | 'expenses' | 'safety' | 'documents' | 'collaboration';

export type NotificationChannelPreferences = {
  push?: boolean;    // default true
  email?: boolean;   // default true
  inApp?: boolean;   // default true
};

export type NotificationPreference = {
  // Category toggles (existing)
  itinerary?: boolean;
  expenses?: boolean;
  safety?: boolean;
  documents?: boolean;
  collaboration?: boolean;
  
  // Channel toggles
  channels?: NotificationChannelPreferences;
  
  // Quiet hours (0-23 hour format)
  quietHoursStart?: number; // e.g. 22 for 10pm
  quietHoursEnd?: number;   // e.g. 7 for 7am
};

export type UserPreferences = {
  safety?: {
    /** When true, Safety news is filtered to items that match trip cities, regional terms, or travel-impact + country. */
    strictDestinationNews?: boolean;
  };
};

export type UserProfile = {
  id?: string;
  name?: string;
  homeCountry?: string;
  /** ISO 3166-1 alpha-2 (e.g. "NG") */
  homeCountryIso2?: string;
  homeCity?: string;
  citizenship?: string;
  /** ISO 3166-1 alpha-2 (e.g. "NG") */
  citizenshipIso2?: string;
  language?: string;
  avatar?: AvatarSelection;
  travelType?: TravelType;
  /** Legacy gate used throughout UI. Derived from entitlement fields below. */
  isPremium?: boolean;
  /** Normalized plan tier used by UI and backend-driven access checks. */
  planTier?: PlanTier;
  /** Rich entitlement state synchronized from billing provider webhooks. */
  entitlement?: PlanEntitlement;
  notificationPreference?: NotificationPreference;
  preferences?: UserPreferences;
  /** User's preferred default currency for budgets and expense tracking */
  defaultCurrency?: string;
  /** Trip IDs that the user has muted (no push/email notifications) */
  mutedTrips?: string[];
};

/** ISO 4217 code for flight/hotel search and price display; defaults to USD if unset or invalid. */
export function resolvePreferredCurrency(profile?: Pick<UserProfile, 'defaultCurrency'> | null): string {
  const raw = profile?.defaultCurrency?.trim();
  if (!raw) return 'USD';
  const upper = raw.toUpperCase();
  return /^[A-Z]{3}$/.test(upper) ? upper : 'USD';
}

export type FirebaseUserInfo = {
  uid: string;
  email?: string | null;
};

type UserState = {
  authReady: boolean;
  firebaseUser?: FirebaseUserInfo;
  isAuthenticated: boolean;
  needsEmailVerification: boolean;
  hasCompletedOnboarding: boolean;
  hasCompletedTutorial: boolean;
  shouldShowTutorial: boolean;
  isNewUser: boolean;
  profile: UserProfile;
  lastCompletedTripId?: string | null;
  lastCompletedEntryPoint?: string | null;
};

type UserActions = {
  setAuthReady: (ready: boolean) => void;
  setFirebaseUser: (user?: FirebaseUserInfo) => void;
  setNeedsEmailVerification: (needsVerification: boolean) => void;
  hydrateProfile: (profile: UserProfile, hasCompleted: boolean, isNew: boolean) => void;
  setProfileDetails: (details: Partial<UserProfile>) => void;
  setAvatar: (avatar: AvatarSelection) => void;
  setTravelType: (travelType?: TravelType) => void;
  completeOnboarding: () => void;
  completeTutorial: () => void;
  setShouldShowTutorial: (show: boolean) => void;
  setLastCompletedTripId: (tripId?: string | null, entryPoint?: string | null) => void;
  reset: () => void;
};

const initialState: UserState = {
  authReady: false,
  firebaseUser: undefined,
  isAuthenticated: false,
  needsEmailVerification: false,
  hasCompletedOnboarding: false,
  hasCompletedTutorial: false,
  shouldShowTutorial: false,
  isNewUser: true,
  profile: {},
  lastCompletedTripId: null,
  lastCompletedEntryPoint: null,
};

const isStatusPremium = (status?: string): boolean =>
  status === 'active' || status === 'grace_period';

const normalizePremiumFlag = (profile: UserProfile): boolean =>
  Boolean(profile.isPremium) ||
  profile.planTier === 'premium' ||
  isStatusPremium(profile.entitlement?.status);

export const useUserStore = create<UserState & UserActions>((set) => ({
  ...initialState,
  setAuthReady: (ready) => set(() => ({ authReady: ready })),
  setFirebaseUser: (user) =>
    set((state) => ({
      firebaseUser: user,
      isAuthenticated: Boolean(user),
      needsEmailVerification: user ? state.needsEmailVerification : false,
      isNewUser: user ? state.isNewUser : true,
      hasCompletedOnboarding: user ? state.hasCompletedOnboarding : false,
      hasCompletedTutorial: user ? state.hasCompletedTutorial : false,
      shouldShowTutorial: user ? state.shouldShowTutorial : false,
      profile: user ? state.profile : {},
      lastCompletedTripId: user ? state.lastCompletedTripId : null,
      lastCompletedEntryPoint: user ? state.lastCompletedEntryPoint : null,
    })),
  setNeedsEmailVerification: (needsEmailVerification) =>
    set(() => ({ needsEmailVerification })),
  hydrateProfile: (profile, hasCompleted, isNew) =>
    set((state) => ({
      profile: {
        ...profile,
        isPremium: normalizePremiumFlag(profile),
      },
      hasCompletedOnboarding: hasCompleted,
      isNewUser: isNew,
      // Do NOT auto-enable tutorial here. Tutorial eligibility is controlled by Firestore
      // (postOnboardingTutorialEligible) and explicit actions like completeOnboarding().
      shouldShowTutorial: state.shouldShowTutorial,
    })),
  setProfileDetails: (details) =>
    set((state) => {
      const mergedProfile: UserProfile = {
        ...state.profile,
        ...details,
      };
      return {
        profile: {
          ...mergedProfile,
          isPremium: normalizePremiumFlag(mergedProfile),
        },
      };
    }),
  setAvatar: (avatar) =>
    set((state) => ({
      profile: {
        ...state.profile,
        avatar,
      },
    })),
  setTravelType: (travelType) =>
    set((state) => ({
      profile: {
        ...state.profile,
        travelType,
      },
    })),
  setLastCompletedTripId: (tripId, entryPoint) =>
    set(() => ({
      lastCompletedTripId: tripId ?? null,
      lastCompletedEntryPoint: entryPoint ?? null,
    })),
  completeOnboarding: () => {
    void (async () => {
      try {
        const existing = await AsyncStorage.getItem(STORAGE_ONBOARDING_COMPLETED_AT_MS);
        if (!existing) {
          await AsyncStorage.setItem(STORAGE_ONBOARDING_COMPLETED_AT_MS, String(Date.now()));
        }
      } catch {
        // ignore storage failures (upsell timing falls back to no extra deferral)
      }
    })();
    set((state) => ({
      hasCompletedOnboarding: true,
      isNewUser: false,
      // Show tutorial after onboarding if not already completed
      shouldShowTutorial: !state.hasCompletedTutorial,
    }));
  },
  completeTutorial: () =>
    set(() => ({
      hasCompletedTutorial: true,
      shouldShowTutorial: false,
    })),
  setShouldShowTutorial: (show) =>
    set(() => ({
      shouldShowTutorial: show,
    })),
  reset: () =>
    set(() => ({
      ...initialState,
      authReady: true,
    })),
}));

type UserStore = UserState & UserActions;

export const selectIsAuthenticated = (state: UserStore) => state.isAuthenticated;
export const selectNeedsEmailVerification = (state: UserStore) => state.needsEmailVerification;
export const selectHasCompletedOnboarding = (state: UserStore) => state.hasCompletedOnboarding;
export const selectIsNewUser = (state: UserStore) => state.isNewUser;
export const selectUserProfile = (state: UserStore) => state.profile;
export const selectAuthReady = (state: UserStore) => state.authReady;
export const selectFirebaseUser = (state: UserStore) => state.firebaseUser;

export type { UserStore };


